import { memo, ReactElement, useCallback, useMemo, useRef } from 'react';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Table, TableComponent, TableProps } from '@ohpen/enso-ui';
import { ExclusivitySetResponse, ActionEntityType } from '@pmp/api';
import { Guard } from '@ohpen/react-authz';
import { lockActionTitle } from '@pmp/utils';
import { ListProps } from '@pmp/types';

import { exclusiveSetsColumns } from '../tableConfig';

const entityType = ActionEntityType.EXCLUSIVITY_SETS;

export const ExclusiveProductsList = memo(
  ({ isFetching, valueLists, data, onCreate, onView, onEdit, onDelete }: ListProps<ExclusivitySetResponse>): ReactElement => {
    const tableRef = useRef<TableComponent | null>(null);
    const { t } = useTranslation();

    const getRowActions: (record: ExclusivitySetResponse) => ReactElement[] = useCallback(
      record => {
        const rowActions = [
          <Tooltip key="view" title={t('global.buttons.view')} placement="top">
            <Button type="text" aria-label="View exclusives" icon={<EyeOutlined rev={undefined} />} onClick={() => onView(record)} />
          </Tooltip>,
          <Guard key="edit" permissions={['exclusivitySets:update']} flags={['enableExclusivesCrud']}>
            <Tooltip title={lockActionTitle('edit', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Edit exclusives"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
          <Guard key="delete" permissions={['exclusivitySets:delete']} flags={['enableExclusivesCrud']}>
            <Tooltip title={lockActionTitle('delete', record.lockedByActionId)} placement="top">
              <Button
                type="text"
                aria-label="Delete exclusives"
                icon={<DeleteOutlined key="delete" />}
                onClick={() => onDelete(record)}
                disabled={!!record.lockedByActionId}
              />
            </Tooltip>
          </Guard>,
        ];

        return rowActions;
      },
      [onDelete, onEdit, onView, t],
    );

    const tableProps: TableProps<ActionEntityType, ExclusivitySetResponse> = useMemo(
      () => ({
        id: entityType,
        ref: tableRef,
        dataSource: data ?? [],
        columns: exclusiveSetsColumns,
        rowKey: 'id',
        pagination: true,
        scroll: { x: 'max-content', y: '100%' },
        valueLists,
        loading: isFetching,
        getRowActions,
        showViews: true,
      }),
      [data, valueLists, isFetching, getRowActions],
    );

    return (
      <Table {...tableProps}>
        <Table.ActionsBar>
          <Flex justify="space-between">
            <Space size="middle">
              <Table.GlobalSearch />
            </Space>
            <Space size="middle">
              <Table.ResetFiltersButton />
              <Table.SelectColumnsButton />
              <Table.ExportButton
                fileName={`Exclusive Sets ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                sheetName="Exclusive Sets"
                disabled={isFetching}
                exportView
              />
              <Guard permissions={['exclusivitySets:create']} flags={['enableExclusivesCrud']}>
                <Table.CreateButton onClick={onCreate} />
              </Guard>
            </Space>
          </Flex>
        </Table.ActionsBar>
      </Table>
    );
  },
);
